import React from 'react';
import { AlertTriangle } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import SectionHeading from '../components/SectionHeading';
import SEO from '../components/SEO';

function TermsConditionsPage() {
  const effectiveDate = 'October 2, 2026';

  const sections: { heading: string; body: React.ReactNode }[] = [
    {
      heading: '1. Acceptance of Terms',
      body: (
        <div className="space-y-3">
          <p>
            These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access to and use of the Impresstars website
            located at impresstars.com (the &ldquo;Site&rdquo;), as well as any services, consultations, proposals, or
            deliverables we provide (collectively, the &ldquo;Services&rdquo;). By accessing the Site, submitting an
            inquiry, or engaging Impresstars for any project, you acknowledge that you have read, understood, and agree
            to be bound by these Terms.
          </p>
          <p>
            If you are entering into an agreement on behalf of a company or organization, you represent and warrant that
            you have the authority to bind that entity to these Terms. If you do not agree with any part of these Terms,
            you must not access the Site or engage our Services.
          </p>
          <p>
            These Terms constitute the entire agreement between you and Impresstars with respect to the Site and general
            use of our Services, except where a separate signed proposal, statement of work, or service agreement
            expressly overrides a provision here. In the event of a conflict between these Terms and a signed agreement,
            the signed agreement prevails for that specific engagement only.
          </p>
        </div>
      ),
    },
    {
      heading: '2. No Legal Advice — Not a Law Firm',
      body: (
        <div className="space-y-3">
          <p className="text-[#FDCB12] font-semibold">
            IMPORTANT: Impresstars is a business consulting, technology, and creative services company. We are not a
            law firm, and we do not provide legal advice, legal opinions, or legal representation of any kind.
          </p>
          <p>
            Any information we provide related to business structure, incorporation, contracts, intellectual property,
            privacy compliance, tax, or regulatory matters is provided for general informational purposes only and
            does not constitute legal, tax, or accounting advice. Such information is not a substitute for advice from
            a qualified professional licensed in your jurisdiction.
          </p>
          <p>
            You should consult a licensed lawyer, accountant, or tax professional before making decisions that have
            legal or tax implications. Impresstars does not accept any responsibility or liability for decisions you
            make — or fail to make — based on information we provide. No solicitor-client or attorney-client
            relationship is created by your use of the Site, submission of an inquiry, or engagement of our Services.
          </p>
        </div>
      ),
    },
    {
      heading: '3. Description of Services',
      body: (
        <div className="space-y-3">
          <p>
            Impresstars provides business and technology services including, but not limited to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Business incorporation support and guidance (administrative, not legal);</li>
            <li>AI solutions, automation, and software development;</li>
            <li>B2B solutions and operational consulting;</li>
            <li>Marketing strategy, brand design, and creative services;</li>
            <li>Business software selection, implementation, and integration;</li>
            <li>Referral links to third-party tools and platforms we recommend.</li>
          </ul>
          <p>
            The specific scope, deliverables, timeline, and fees for any project are defined in a separate proposal,
            statement of work, or signed service agreement (&ldquo;Engagement Agreement&rdquo;). We reserve the right
            to decline or refuse any project at our discretion before an Engagement Agreement is signed.
          </p>
        </div>
      ),
    },
    {
      heading: '4. Proposals, Quotes, and Engagement',
      body: (
        <div className="space-y-3">
          <p>
            Any proposal, quote, or estimate we provide is valid for thirty (30) days from the date issued unless
            otherwise stated. Quotes are based on the scope described at the time of quoting; if the project scope
            changes after a quote is accepted, additional fees may apply and will be communicated before work
            proceeds.
          </p>
          <p>
            An Engagement Agreement between you and Impresstars is formed only when both parties have signed the
            proposal or statement of work and the initial deposit or first invoice has been paid. No work will begin
            until these conditions are met unless explicitly agreed in writing.
          </p>
          <p>
            Any timeframe or delivery date we provide is an estimate based on current information and our workload.
            While we make every reasonable effort to meet stated timelines, we are not liable for delays caused by
            factors outside our control, including but not limited to client feedback delays, third-party service
            outages, or scope changes.
          </p>
        </div>
      ),
    },
    {
      heading: '5. Client Responsibilities',
      body: (
        <div className="space-y-3">
          <p>To enable us to deliver the Services effectively, you agree to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide accurate, complete, and timely information when requested;</li>
            <li>Respond to review requests, feedback requests, and approvals within a reasonable timeframe;</li>
            <li>Provide access to systems, accounts, or materials necessary for the project;</li>
            <li>Ensure you have the legal right to share any content, data, or materials you provide to us;</li>
            <li>Not use the Site or Services for any unlawful, fraudulent, or abusive purpose;</li>
            <li>Not attempt to disrupt, reverse-engineer, or compromise the security of the Site or any system we maintain.</li>
          </ul>
          <p>
            Delays caused by the client (including late feedback, missing assets, or scope additions) may extend
            project timelines and, if significant, may result in additional fees as communicated.
          </p>
        </div>
      ),
    },
    {
      heading: '6. Payment Terms',
      body: (
        <div className="space-y-3">
          <p>
            Fees, payment schedule, and accepted methods are specified in the Engagement Agreement. Unless otherwise
            stated, the following standard payment terms apply:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>A deposit of fifty percent (50%) of the total project fee is due before work begins;</li>
            <li>The remaining balance is due upon project completion and before final deliverables are transferred;</li>
            <li>Ongoing services (maintenance, retainers) are invoiced monthly in advance;</li>
            <li>All invoices are due within fifteen (15) days of the invoice date;</li>
            <li>Late payments may incur interest at 1.5% per month (18% annually) on the outstanding amount;</li>
            <li>We may suspend or pause work if an invoice is more than fifteen (15) days overdue.</li>
          </ul>
          <p>
            Unless stated otherwise, fees do not include third-party costs such as domain registration, hosting,
            software licenses, stock media, or advertising spend. These are billed to you directly or passed through
            at cost with supporting documentation.
          </p>
        </div>
      ),
    },
    {
      heading: '7. Refund Policy',
      body: (
        <div className="space-y-3">
          <p>
            Because our Services are customized and work begins upon engagement, the initial deposit is non-refundable
            once work has commenced. This covers the time, resources, and scheduling committed to your project.
          </p>
          <p>
            If you cancel a project before work has begun and within five (5) business days of signing the Engagement
            Agreement, the deposit will be refunded in full minus any third-party costs already incurred on your
            behalf.
          </p>
          <p>
            If a project is cancelled mid-engagement, you are responsible for payment of all work completed up to the
            date of cancellation, calculated on a time-and-materials basis at the rates specified in the Engagement
            Agreement. Any prepaid amounts exceeding the value of work completed will be refunded.
          </p>
          <p>
            Ongoing monthly retainers may be cancelled with thirty (30) days written notice. The current month&rsquo;s
            fee is non-refundable, but no further charges will be applied.
          </p>
        </div>
      ),
    },
    {
      heading: '8. Intellectual Property',
      body: (
        <div className="space-y-3">
          <p><strong>Our website content.</strong> All content on the Site — including text, graphics, logos, design
          elements, code, and media — is owned by or licensed to Impresstars and is protected by Canadian and
          international intellectual property laws. You may not copy, reproduce, distribute, or create derivative
          works from the Site without our prior written consent.</p>
          <p><strong>Project deliverables.</strong> Upon receipt of full payment, ownership of the final, custom
          deliverables created specifically for your project (such as a completed website, brand assets, or software)
          transfers to you, to the extent permitted by the Engagement Agreement. This transfer does not include:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Third-party tools, libraries, frameworks, or open-source components, which remain under their respective licenses;</li>
            <li>Pre-existing templates, code, or assets we developed before or independently of your project;</li>
            <li>Our internal methodologies, processes, and proprietary tools used to deliver the work;</li>
            <li>Stock media, fonts, or licensed components, which are governed by their third-party licenses.</li>
          </ul>
          <p><strong>Client-provided materials.</strong> You retain ownership of all content and materials you provide
          to us. By providing them, you grant us a non-exclusive licence to use, copy, and modify them solely for the
          purpose of delivering the Services. You represent and warrant that you have all necessary rights to grant
          this licence and that your materials do not infringe the rights of any third party.</p>
        </div>
      ),
    },
    {
      heading: '9. Confidentiality',
      body: (
        <div className="space-y-3">
          <p>
            Both parties agree to keep confidential any non-public information shared during the course of a project,
            including business plans, financial data, customer lists, trade secrets, and proprietary processes.
            Confidential information will not be disclosed to third parties without prior written consent, except to
            employees, contractors, or service providers who need access to perform the work and are bound by
            equivalent confidentiality obligations.
          </p>
          <p>
            Confidentiality obligations do not apply to information that is already public, independently developed,
            rightfully received from a third party, or required to be disclosed by law or court order.
          </p>
        </div>
      ),
    },
    {
      heading: '10. Third-Party Tools, Links, and Referrals',
      body: (
        <div className="space-y-3">
          <p>
            The Site may contain links to third-party websites, tools, and platforms. We are not responsible for the
            content, accuracy, security, or privacy practices of those external sites. Your use of third-party
            services is at your own risk and subject to their respective terms and privacy policies.
          </p>
          <p>
            Some links on our Site and in our recommendations are referral or affiliate links. If you sign up for a
            third-party service through one of these links, Impresstars may earn a commission or referral fee at no
            additional cost to you. We only recommend tools and platforms we believe are valuable, but we do not
            guarantee their performance, availability, or suitability for your specific needs. Your relationship with
            any third-party provider is separate from your relationship with Impresstars.
          </p>
        </div>
      ),
    },
    {
      heading: '11. Disclaimer of Warranties',
      body: (
        <div className="space-y-3">
          <p>
            The Site and Services are provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. To the
            fullest extent permitted by law, Impresstars disclaims all warranties, whether express or implied,
            including but not limited to implied warranties of merchantability, fitness for a particular purpose,
            non-infringement, and any warranty that the Services will be error-free, uninterrupted, or achieve a
            specific result.
          </p>
          <p>
            We do not guarantee any specific business outcome, revenue increase, search engine ranking, or return on
            investment from the Services. Results depend on many factors outside our control, including market
            conditions, your business decisions, and the actions of third parties.
          </p>
          <p>
            Any advice, recommendations, or strategies we provide are based on our professional experience and the
            information available at the time. They are not guarantees of future performance.
          </p>
        </div>
      ),
    },
    {
      heading: '12. Limitation of Liability',
      body: (
        <div className="space-y-3">
          <p>
            To the fullest extent permitted by applicable law, Impresstars, its owners, employees, contractors, and
            partners shall not be liable for any indirect, incidental, special, consequential, or punitive damages
            arising from or related to your use of the Site or Services, including but not limited to loss of profits,
            loss of data, loss of business, business interruption, or damage to reputation, even if we have been
            advised of the possibility of such damages.
          </p>
          <p>
            Our total aggregate liability for any claim arising from or related to the Services shall not exceed the
            total amount you paid us for the specific project or engagement that is the subject of the claim, or one
            thousand Canadian dollars (CAD $1,000), whichever is less.
          </p>
          <p>
            You acknowledge that the fees charged reflect this allocation of risk and that the Services would not be
            economically feasible without these limitations.
          </p>
        </div>
      ),
    },
    {
      heading: '13. Indemnification',
      body: (
        <div className="space-y-3">
          <p>
            You agree to indemnify, defend, and hold harmless Impresstars, its owners, employees, contractors, and
            partners from and against any and all claims, liabilities, damages, losses, and expenses (including
            reasonable legal fees) arising from:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Your breach of these Terms or any Engagement Agreement;</li>
            <li>Your misuse of the Site or Services;</li>
            <li>Any content or materials you provide that infringes the rights of a third party;</li>
            <li>Any inaccurate or misleading information you provided to us; or</li>
            <li>Your violation of any applicable law or regulation.</li>
          </ul>
        </div>
      ),
    },
    {
      heading: '14. Termination',
      body: (
        <div className="space-y-3">
          <p>
            You may terminate an engagement at any time by providing written notice, subject to the payment
            obligations in the refund and payment sections above.
          </p>
          <p>
            We may terminate or suspend an engagement or your access to the Site immediately if you breach these
            Terms, fail to pay invoices when due, engage in unlawful or abusive conduct, or if continued engagement
            would create a conflict of interest or expose us to legal or reputational risk.
          </p>
          <p>
            Upon termination, you remain responsible for all fees accrued up to the date of termination. Sections
            relating to intellectual property, confidentiality, limitation of liability, indemnification, and
            governing law survive any termination.
          </p>
        </div>
      ),
    },
    {
      heading: '15. Governing Law and Dispute Resolution',
      body: (
        <div className="space-y-3">
          <p>
            These Terms and any dispute arising from them are governed by the laws of the Province of Ontario and the
            federal laws of Canada applicable therein, without regard to conflict-of-law principles. The courts of
            Ontario shall have exclusive jurisdiction over any disputes.
          </p>
          <p>
            Before initiating litigation, both parties agree to attempt in good faith to resolve any dispute through
            negotiation or mediation. If the dispute cannot be resolved within sixty (60) days, either party may
            proceed to the courts of Ontario.
          </p>
          <p>
            Impresstars operates from London, Ontario, Canada. If you are accessing the Site from outside Canada, you
            are responsible for compliance with any local laws applicable to your use of the Site and engagement of
            our Services.
          </p>
        </div>
      ),
    },
    {
      heading: '16. Changes to These Terms',
      body: (
        <div className="space-y-3">
          <p>
            We may update these Terms from time to time to reflect changes in our practices, legal requirements, or
            business operations. When we make material changes, we will update the &ldquo;Effective date&rdquo; at the
            top of this page and, where appropriate, notify clients with active engagements by email.
          </p>
          <p>
            Your continued use of the Site or Services after the effective date of revised Terms constitutes your
            acceptance of the updated Terms. If you do not agree to the revised Terms, you must discontinue use of the
            Site and cancel any ongoing engagement in accordance with the termination provisions above.
          </p>
          <p>
            For existing Engagement Agreements that have been signed, the Terms in effect at the time of signing
            continue to govern that specific engagement unless both parties agree otherwise in writing.
          </p>
        </div>
      ),
    },
    {
      heading: '17. Contact',
      body: (
        <div className="space-y-3">
          <p>
            If you have any questions or concerns about these Terms &amp; Conditions, please contact us:
          </p>
          <ul className="list-none space-y-2">
            <li><span className="text-gray-400">Email:</span> <a href="mailto:info@impresstars.com" className="text-[#FDCB12] hover:text-[#FF5500] transition">info@impresstars.com</a></li>
            <li><span className="text-gray-400">Website:</span> impresstars.com</li>
            <li><span className="text-gray-400">Location:</span> London, Ontario, Canada</li>
          </ul>
          <p className="text-sm text-gray-500 pt-2">
            Please include &ldquo;Terms Inquiry&rdquo; in the subject line of your email so we can route it
            promptly.
          </p>
        </div>
      ),
    },
  ];

  return (
    <>
      <SEO
        title="Terms & Conditions"
        description="Terms and Conditions for using Impresstars services and website, including payment terms, intellectual property, and limitation of liability."
        canonicalPath="/terms-conditions"
      />
      <section className="py-20 px-4 md:px-[5vw] text-white">
        <div className="container mx-auto max-w-4xl">
          <SectionHeading eyebrow="Legal" title="Terms & Conditions" />
          <AnimatedSection animation="fade-in-up" delay={100}>
            <p className="text-gray-300 mb-8 text-center">Effective date: {effectiveDate}</p>
          </AnimatedSection>

          <AnimatedSection animation="fade-in-up" delay={150}>
            <div className="mb-8 bg-amber-500/10 border border-amber-500/30 rounded-lg p-6 flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-amber-300 mb-1">Not a Law Firm</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Impresstars provides business, technology, and creative services. We are not a law firm and do not
                  provide legal advice. These Terms are not a substitute for independent legal advice. Please consult
                  a licensed lawyer if you need legal guidance on any matter.
                </p>
              </div>
            </div>
          </AnimatedSection>

          <div className="space-y-6">
            {sections.map((section, index) => (
              <AnimatedSection key={section.heading} animation="fade-in-up" delay={((index % 5) + 1) * 100 as 100 | 200 | 300 | 400 | 500}>
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
                  <h2 className="text-2xl font-semibold text-white mb-3">{section.heading}</h2>
                  <div className="text-gray-300 leading-relaxed space-y-2">{section.body}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection animation="fade-in-up" delay={200}>
            <p className="mt-8 text-sm text-gray-500 text-center max-w-2xl mx-auto">
              This document is provided for general informational purposes and does not constitute legal advice. It
              was last updated on {effectiveDate}. If you have specific legal questions about these Terms, please
              consult a qualified attorney in your jurisdiction.
            </p>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
}

export default TermsConditionsPage;
